import { useQueries } from '@tanstack/react-query'
import { useMemo } from 'react'

import type { CoinItemInGetList, CoinItemInPostList } from '@/services/coin/coins'
import { coinMarketQueryOptions } from '@/services/coin/market'
import type { CoinSlug } from '@/types/coin'

/**
 * Get restricted_areas and visibility for coins using individual API calls
 * - For CoinItemInPostList: restricted_areas is already available
 * - For CoinItemInGetList: fetch via /token-price/{slug} (only endpoint with restricted_areas)
 *
 * Note: Uses useQueries with aggressive caching to minimize API calls
 *
 * Returns:
 * - restrictedAreasMap: slug -> restricted_areas[]
 * - visibleCoins: coins filtered to exclude visibility === 'internal'
 */
export function useCoinRestrictedAreas(coins: (CoinItemInGetList | CoinItemInPostList)[]): {
  restrictedAreasMap: Map<CoinSlug, string[]>
  visibleCoins: (CoinItemInGetList | CoinItemInPostList)[]
} {
  const hasRestrictedAreas = coins.length > 0 && 'restricted_areas' in coins[0]

  const slugs = useMemo(
    () => (hasRestrictedAreas ? [] : coins.map(coin => coin.slug)),
    [coins, hasRestrictedAreas],
  )

  const queries = useQueries({
    queries: slugs.map(slug => ({
      ...coinMarketQueryOptions(slug, true), // silent404 = true
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
    })),
  })

  const result = useMemo(() => {
    const restrictedAreasMap = new Map<CoinSlug, string[]>()
    const internalSlugs = new Set<CoinSlug>()

    if (hasRestrictedAreas) {
      coins.forEach(coin => {
        const postListCoin = coin as CoinItemInPostList
        restrictedAreasMap.set(postListCoin.slug, postListCoin.restricted_areas ?? [])
      })
    } else {
      // Use fetched data from queries
      queries.forEach((query, index) => {
        const slug = slugs[index]
        if (query.data?.restricted_areas) {
          restrictedAreasMap.set(slug, query.data.restricted_areas)
        } else {
          restrictedAreasMap.set(slug, [])
        }
        if (query.data && query.data.visibility === 'internal') {
          internalSlugs.add(slug)
        }
      })
    }

    return {
      restrictedAreasMap,
      visibleCoins: coins.filter(coin => !internalSlugs.has(coin.slug)),
    }
  }, [
    coins,
    hasRestrictedAreas,
    slugs,
    queries.map(q => q.data?.restricted_areas?.join(',')).join('|'),
    queries.map(q => q.data?.visibility ?? '').join('|'),
  ])

  return result
}
